import { API_CONFIG, apiFetch, handleApiResponse } from './api';
import { getAccessToken } from './oidc';
import { isCaptureEnabled, disableCapture, saveResponse, getResponse, hasResponse, isCachingEnabled } from './CacheService';

// API types
export interface ValueStream {
  id: string;
  label: string;
  description: string;
}

export interface Model {
  id: string;
  label: string;
}

export interface Outcome {
  id: string;
  label: string;
  description?: string | null;
  type?: string;
}

export interface ValueStreamDetails {
  valueStream: {
    id: string;
    label: string;
    description: string;
    type: string;
  };
  stages: {
    id: string;
    label: string;
    description: string;
    sortOrder: number;
  }[];
  capabilities: {
    id: string;
    label: string;
    description: string;
    stageId: string;
    stageName: string;
  }[];
}

/**
 * Extract models from JWT token
 */
export function extractModelsFromToken(token: string): Model[] {
  try {
    // Split the token into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error("Invalid JWT token format - should have 3 parts");
      return [];
    }
      
    // Get the payload part (second part)
    const base64Url = parts[1];
    
    // Convert base64url to base64
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    const paddedBase64 = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    
    // Decode the base64 string
    let jsonPayload;
    try {
      // For browsers
      jsonPayload = decodeURIComponent(
        Array.from(atob(paddedBase64))
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
    } catch (e) {
      console.error("Browser atob failed, using fallback:", e);
      return [];
    }
    
    // Parse the payload
    const payload = JSON.parse(jsonPayload);
    
    // Check if models array exists in the payload
    if (!payload.models || !Array.isArray(payload.models)) {
      console.warn("No models found in token payload");
      return [];
    }
    
    return payload.models;
  } catch (error) {
    console.error("Error extracting models from token:", error);
    return [];
  }
}

/**
 * Create a service with the active model ID.
 *
 * All Capsifi calls are proxied through the BFF and authenticated with the
 * signed-in user's OIDC access token, which apiFetch attaches as a Bearer token
 * (and refreshes proactively). No client-credentials token is fetched or stored.
 */
export const createCapsifiService = (activeModelId: string) => {
  /** Headers shared by every Capsifi request. */
  const buildHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
      'Content-Type': API_CONFIG.headers.contentType,
    };

    if (activeModelId) {
      headers[API_CONFIG.headers.modelId] = activeModelId;
    }

    return headers;
  };

  const readError = async (response: Response, fallback: string): Promise<string> => {
    try {
      const errorData = await response.json();
      return errorData.error || errorData.message || fallback;
    } catch {
      return fallback;
    }
  };

  return {
    /**
     * Verify the user has a usable session token for Capsifi calls.
     */
    testConnection: async (): Promise<boolean> => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Not signed in. Please sign in to connect to Capsifi.');
      }

      return true;
    },

    /**
     * Get available models from the signed-in user's token.
     */
    getModels: async (): Promise<Model[]> => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Not signed in. Please sign in to load available models.');
      }

      return extractModelsFromToken(token);
    },

    /**
     * Get all value streams
     */
    getValueStreams: async (): Promise<ValueStream[]> => {
      try {
        if (isCachingEnabled() && hasResponse('valueStreams')) {
          console.debug('Returning cached value streams');
          return getResponse<ValueStream[]>('valueStreams') as ValueStream[];
        }

        const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.capsifiValueStreams}`;
        const response = await apiFetch(url, { method: 'GET', headers: buildHeaders() });

        if (!response.ok) {
          throw new Error(await readError(response, `Server error: ${response.status} ${response.statusText}`));
        }

        const result = await handleApiResponse<ValueStream[]>(response);
        if (isCaptureEnabled('valueStreams')) {
          saveResponse('valueStreams', result);
          disableCapture('valueStreams');
        }
        return result;
      } catch (error) {
        console.error('Error fetching value streams:', error);
        throw error;
      }
    },

    /**
     * Get detailed information for a specific value stream
     */
    getValueStreamDetails: async (valueStreamId: string): Promise<ValueStreamDetails> => {
      try {
        if (isCachingEnabled() && hasResponse('valueStreamDetails')) {
          console.debug('Returning cached value stream details');
          return getResponse<ValueStreamDetails>('valueStreamDetails') as ValueStreamDetails;
        }

        // Only replace # with %23, leave the rest of the URL untouched
        const encodedId = valueStreamId.replace(/#/g, '%23');
        const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.capsifiValueStreamDetails}?id=${encodedId}`;
        const response = await apiFetch(url, { method: 'GET', headers: buildHeaders() });

        if (!response.ok) {
          throw new Error(await readError(response, `Server error: ${response.status} ${response.statusText}`));
        }

        const result = await handleApiResponse<ValueStreamDetails>(response);
        if (isCaptureEnabled('valueStreamDetails')) {
          saveResponse('valueStreamDetails', result);
          disableCapture('valueStreamDetails');
        }
        return result;
      } catch (error) {
        console.error('Error fetching value stream details:', error);
        throw error;
      }
    },

    /**
     * Get capabilities for a model
     */
    getCapabilities: async (): Promise<any[]> => {
      try {
        const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.capsifiCapabilities}`;
        const response = await apiFetch(url, { method: 'GET', headers: buildHeaders() });

        if (!response.ok) {
          throw new Error(await readError(response, `Server error: ${response.status} ${response.statusText}`));
        }

        return handleApiResponse<any[]>(response);
      } catch (error) {
        console.error('Error fetching capabilities:', error);
        throw error;
      }
    },

    /**
     * Get outcomes from the API
     */
    getOutcomes: async (): Promise<Outcome[]> => {
      try {
        if (isCachingEnabled() && hasResponse('outcomes')) {
          console.debug('Returning cached outcomes');
          return getResponse<Outcome[]>('outcomes') as Outcome[];
        }

        const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.capsifiOutcomes}`;
        const response = await apiFetch(url, { method: 'GET', headers: buildHeaders() });

        if (!response.ok) {
          throw new Error(await readError(response, `Server error: ${response.status} ${response.statusText}`));
        }

        const result = await handleApiResponse<Outcome[]>(response);
        if (isCaptureEnabled('outcomes')) {
          saveResponse('outcomes', result);
          disableCapture('outcomes');
        }
        return result;
      } catch (error) {
        console.error('Error fetching outcomes:', error);
        throw error;
      }
    },

    /**
     * Get value stream transitions data
     * This method fetches transition data that shows connections between tasks and outcomes
     * in a value stream, which can be used to create flow edges in a graph visualization
     */
    getValueStreamTransitions: async (options?: { itemId?: string; filters?: string }): Promise<any> => {
      try {
        if (isCachingEnabled() && hasResponse('outcomes')) {
          console.debug('Returning cached value stream transitions (outcomes)');
          return getResponse<any>('outcomes');
        }

        // Default grid ID for value stream transitions
        const defaultGridId = 'http://capsi.com.au/StrategicPlanningReports#ValueStreamOutcomeTransitions';
        const defaultItemId = 'http://orbus_busarch.capsi.com.au/orbus_busarch/SP/Model#da671ee7-8b6e-4eae-9995-632971ac724f_VALUESTREAM_Sales_Pipelin';

        const gridId = defaultGridId; // Always use the transitions grid ID
        const itemId = options?.itemId || defaultItemId;
        const filters = options?.filters || '{}';

        const params = new URLSearchParams({ gridId, itemId, filters });

        // Use the generic grid-report endpoint
        const url = `${API_CONFIG.baseUrl}/capsifi/grid-report?${params.toString()}`;
        const response = await apiFetch(url, { method: 'GET', headers: buildHeaders() });
        const result = await handleApiResponse<any>(response);
        if (isCaptureEnabled('outcomes')) {
          saveResponse('outcomes', result);
          disableCapture('outcomes');
        }
        return result;
      } catch (error) {
        console.error('Error in getValueStreamTransitions:', error);
        throw error;
      }
    }
  };
};

// Throw error if used directly to enforce usage through the factory function
export const CapsifiService = () => {
  throw new Error('CapsifiService cannot be used directly. Please use createCapsifiService(modelId) instead.');
};